import {
  type RefObject,
  type FocusEvent as ReactFocusEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import { type DateTimeInputRef } from '../../date-time-range-picker/types.js';
import type { SpinButtonHandler } from '../../spin-buttons/spin-button-group/types.js';

/*
 * Handles the focus event within 'DateTimeInput' components.
 * Moving the focus to the `DateTimeInput` focuses the 'to input' or the first empty spin button.
 * If everything is empty focus the 'from input'.
 * If the target element is one of the spin buttons or expression, the focus is not redirected.
 * @internal
 */
export const focusDateTimeInput = (
  event: ReactFocusEvent | ReactMouseEvent | null,
  refFrom: RefObject<DateTimeInputRef | null>,
  refTo?: RefObject<DateTimeInputRef | null>,
) => {
  const spinButtonHandlersTo = refTo?.current?.spinButtonHandlers;
  const spinButtonHandlersFrom = refFrom?.current?.spinButtonHandlers;

  /** Check if the target element is one of the spin buttons or expression. */
  const isTargetValid = (target: EventTarget | undefined) => {
    const allInteractiveElements = [
      ...[...(spinButtonHandlersFrom?.values() ?? [])].map(
        (h) => h?.element.current,
      ),
      ...[...(spinButtonHandlersTo?.values() ?? [])].map(
        (h) => h?.element.current,
      ),
      refFrom?.current?.expressionInputRef,
      refTo?.current?.expressionInputRef,
    ];
    return (
      target !== undefined &&
      allInteractiveElements.includes(target as HTMLSpanElement)
    );
  };

  /**
   * Checks if any spin button in the provided handlers is empty (has no textContent).
   * Returns false if all spin buttons are empty or none are empty.
   */
  const isAnySpinButtonEmpty = (
    handlers:
      | Map<Intl.DateTimeFormatPartTypes, SpinButtonHandler | null>
      | undefined,
  ): boolean => {
    if (!handlers) {
      return false;
    }

    let hasEmpty = false;
    let allEmpty = true;

    for (const [, handler] of handlers) {
      const textContent = handler?.element.current?.textContent;
      if (textContent === '') {
        hasEmpty = true;
      } else {
        allEmpty = false;
      }
    }

    return hasEmpty && !allEmpty;
  };

  /**
   * Returns the first empty spin button handler, or the first handler if all
   * are filled. Returns null when handlers is undefined (no input mounted).
   * Used both to locate the focus target for partial-fill cases and to detect
   * whether the spin button group is fully filled (non-empty textContent).
   */
  const findFirstEmptyOrFirst = (
    handlers:
      | Map<Intl.DateTimeFormatPartTypes, SpinButtonHandler | null>
      | undefined,
  ) => {
    if (!handlers) {
      return null;
    }

    let result: SpinButtonHandler | null = null;

    for (const [, handler] of handlers) {
      if (!result) {
        result = handler;
      }
      if (!handler?.element.current?.textContent) {
        result = handler;
        break;
      }
    }

    return result;
  };

  // When data-restoring-selection is active, expressionOnFocusHandler skips selectAll(); move caret to end manually.
  const focusExpressionInput = (el: HTMLSpanElement | null | undefined) => {
    if (!el) {
      return;
    }
    const isRestoringSelection = Boolean(
      el.closest?.('[data-restoring-selection]'),
    );
    el.focus();
    if (isRestoringSelection) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      window.getSelection()?.removeAllRanges();
      window.getSelection()?.addRange(range);
    }
  };

  if (isTargetValid(event?.target)) {
    return;
  }

  const emptyToSpinButton = isAnySpinButtonEmpty(spinButtonHandlersTo);
  const emptyFromSpinButton = isAnySpinButtonEmpty(spinButtonHandlersFrom);

  const firstFromSpinButtonHandler = findFirstEmptyOrFirst(
    spinButtonHandlersFrom,
  );
  const firstToSpinButtonHandler = findFirstEmptyOrFirst(spinButtonHandlersTo);

  // Focus the 'to spin buttons', when they have at least one empty.
  if (emptyToSpinButton && !emptyFromSpinButton && firstToSpinButtonHandler) {
    firstToSpinButtonHandler.element.current?.focus();
    return;
  }

  // Focus the 'from spin buttons' when they have at least one empty.
  if (emptyFromSpinButton) {
    if (firstFromSpinButtonHandler) {
      firstFromSpinButtonHandler?.element.current?.focus();
      return;
    }
  }

  // When 'from' has content (in any mode), route focus to the 'to' input.
  const fromIsFilled =
    refFrom?.current?.expressionInputRef?.textContent !== '' ||
    firstFromSpinButtonHandler?.element.current?.textContent !== '';

  if (fromIsFilled) {
    if (firstToSpinButtonHandler !== null) {
      if (firstToSpinButtonHandler.element.current?.textContent !== '') {
        const lastToHandler = [...(spinButtonHandlersTo?.values() ?? [])].at(
          -1,
        );
        lastToHandler?.element.current?.focus();
        lastToHandler?.setSelection({ start: 0, end: 0 });
      } else {
        focusExpressionInput(refTo?.current?.expressionInputRef);
      }
    } else if (
      firstFromSpinButtonHandler?.element.current?.textContent !== ''
    ) {
      const lastFromHandler = [...(spinButtonHandlersFrom?.values() ?? [])].at(
        -1,
      );
      lastFromHandler?.element.current?.focus();
      lastFromHandler?.setSelection({ start: 0, end: 0 });
    } else {
      focusExpressionInput(refFrom?.current?.expressionInputRef);
    }
    return;
  }
  // Focus the 'from expression input'.
  focusExpressionInput(refFrom?.current?.expressionInputRef);
};

/**
 * Attempts to move the focus to the "hour" spin button of the "from" input.
 * The hour spin button is only foucsable if the value is an absolute timeframe (ISO 8601)
 * and not an expression. If focus is not possible, falls back to {@link focusDateTimeInput}.
 * @param dateTimeFromRef - the ref of the "from" DateTimeInput component
 * @internal
 */
export const focusTimeInput = (
  dateTimeFromRef: RefObject<DateTimeInputRef | null>,
): void => {
  const hourElement =
    dateTimeFromRef.current?.spinButtonHandlers.get('hour')?.element.current;
  if (hourElement) {
    hourElement.focus();
    return;
  }
  focusDateTimeInput(null, dateTimeFromRef);
};

const MAX_FOCUS_ATTEMPTS = 3;

/**
 * Repeatedly attempts to focus an element using requestAnimationFrame until
 * focus succeeds or max attempts are reached. This handles race conditions
 * where popover close animations restore focus to the trigger element.
 * @param getElement - getter that returns the element to focus
 * @param dateTimeRef - optional ref to fall back to via {@link focusDateTimeInput} if all attempts fail
 * @internal
 */
export const focusElementWithRetry = (
  getElement: () => HTMLElement | null | undefined,
  dateTimeRef?: RefObject<DateTimeInputRef | null>,
): void => {
  let attempts = 0;

  const tryFocus = (): void => {
    attempts++;
    const element = getElement();

    if (element) {
      element.focus();
      if (document.activeElement === element) {
        return;
      }
    }

    if (attempts < MAX_FOCUS_ATTEMPTS) {
      requestAnimationFrame(tryFocus);
      return;
    }

    if (dateTimeRef) {
      focusDateTimeInput(null, dateTimeRef);
    }
  };

  requestAnimationFrame(tryFocus);
};
